import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Play, 
  Square, 
  RefreshCw, 
  Zap, 
  Activity, 
  Clock, 
  Trophy, 
  Terminal as TerminalIcon,
  Trash2,
  AlertCircle,
  CheckCircle,
  Info,
  AlertTriangle,
  LogIn,
  Navigation,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status indicator
const StatusIndicator = ({ status }) => {
  const statusStyles = {
    monitoring: "bg-green-500 shadow-[0_0_12px_rgba(34,197,94,0.7)]",
    logged_in: "bg-blue-500 shadow-[0_0_12px_rgba(59,130,246,0.7)]",
    logging_in: "bg-amber-500 shadow-[0_0_12px_rgba(245,158,11,0.7)]",
    navigating: "bg-amber-500 shadow-[0_0_12px_rgba(245,158,11,0.7)]",
    idle: "bg-gray-500 shadow-[0_0_12px_rgba(107,114,128,0.7)]",
    stopped: "bg-red-500 shadow-[0_0_12px_rgba(239,68,68,0.7)]",
    error: "bg-red-600 shadow-[0_0_12px_rgba(220,38,38,0.7)]",
  };

  return (
    <div className="flex items-center gap-2">
      <div 
        className={`w-3 h-3 rounded-full ${statusStyles[status] || statusStyles.idle} ${status === 'monitoring' ? 'animate-pulse' : ''}`}
      />
      <span className="text-sm uppercase tracking-wider font-mono">{status}</span>
    </div>
  );
};

// Log entry
const LogEntry = ({ log }) => {
  const typeIcons = {
    info: <Info className="w-3 h-3 text-blue-400" />,
    success: <CheckCircle className="w-3 h-3 text-green-400" />,
    warning: <AlertTriangle className="w-3 h-3 text-amber-400" />,
    error: <AlertCircle className="w-3 h-3 text-red-400" />,
  };

  const typeColors = {
    info: "text-blue-400",
    success: "text-green-400",
    warning: "text-amber-400",
    error: "text-red-400",
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour12: false });
  };

  return (
    <div className="flex items-start gap-2 py-1 px-2 hover:bg-white/5 border-l-2 border-transparent hover:border-white/20 transition-colors">
      {typeIcons[log.type]}
      <span className="text-gray-500 font-mono text-xs select-none min-w-[70px]">
        {formatTime(log.timestamp)}
      </span>
      <span className={`font-mono text-xs ${typeColors[log.type]}`}>
        {log.message}
      </span>
    </div>
  );
};

// Format uptime
const formatUptime = (seconds) => {
  if (!seconds) return "00:00:00";
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

function App() {
  const [status, setStatus] = useState({
    status: "idle",
    message: "Ready to start",
    last_check: null,
    leads_claimed: 0,
    uptime_seconds: 0,
    current_url: null
  });
  const [logs, setLogs] = useState([]);
  const [leads, setLeads] = useState([]);
  
  // Form state
  const [loginUrl, setLoginUrl] = useState("https://powerplay.generac.com/app/");
  const [email, setEmail] = useState("Tony@actiongenerator.net");
  const [password, setPassword] = useState("1Fish1Cutaia*");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // API calls
  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/automation/status`);
      setStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch status:", error);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/automation/logs?limit=200`);
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    }
  }, []);

  const fetchLeads = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads?limit=50`);
      setLeads(response.data.leads || []);
    } catch (error) {
      console.error("Failed to fetch leads:", error);
    }
  }, []);

  // Step 1: Login
  const doLogin = async () => {
    if (!loginUrl || !email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/automation/login`, { url: loginUrl, email, password });
      toast.success("Login successful!");
      fetchStatus();
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Navigate to PowerPlay
  const doNavigate = async () => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/automation/navigate`);
      toast.success("Navigated to PowerPlay!");
      fetchStatus();
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Navigation failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Start Monitoring
  const startMonitoring = async () => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/automation/start-monitoring`);
      toast.success("Monitoring started! Refreshing every 10 seconds...");
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start monitoring");
    } finally {
      setIsLoading(false);
    }
  };

  // Stop Monitoring
  const stopMonitoring = async () => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/automation/stop-monitoring`);
      toast.success("Monitoring stopped");
      fetchStatus();
    } catch (error) {
      toast.error("Failed to stop monitoring");
    } finally {
      setIsLoading(false);
    }
  };

  // Stop All
  const stopAll = async () => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/automation/stop`);
      toast.success("Session ended");
      fetchStatus();
    } catch (error) {
      toast.error("Failed to stop");
    } finally {
      setIsLoading(false);
    }
  };

  // Clear logs
  const clearLogs = async () => {
    try {
      await axios.delete(`${API}/automation/logs`);
      setLogs([]);
      toast.success("Logs cleared");
    } catch (error) {
      toast.error("Failed to clear logs");
    }
  };

  // Polling
  useEffect(() => {
    fetchStatus();
    fetchLogs();
    fetchLeads();
    
    const statusInterval = setInterval(fetchStatus, 2000);
    const logsInterval = setInterval(fetchLogs, 2000);
    const leadsInterval = setInterval(fetchLeads, 5000);
    
    return () => {
      clearInterval(statusInterval);
      clearInterval(logsInterval);
      clearInterval(leadsInterval);
    };
  }, [fetchStatus, fetchLogs, fetchLeads]);

  // Removed auto-scroll - user can scroll manually now

  const isMonitoring = status.status === "monitoring";
  const isLoggedIn = status.status === "logged_in";
  const isIdle = status.status === "idle";
  const isBusy = status.status === "logging_in" || status.status === "navigating";

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Background */}
      <div className="fixed inset-0 z-0 opacity-5"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1761599821310-da0d6356b4f3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBjeWJlciUyMHRlY2hub2xvZ3klMjBnZW9tZXRyaWMlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3Mjc5OTk2Mnww&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover"
        }}
      />
      
      <Toaster position="top-right" theme="dark" />
      
      <div className="relative z-10 p-6 md:p-8">
        {/* Header */}
        <header className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Zap className="w-8 h-8 text-[#00FF9D]" style={{ filter: "drop-shadow(0 0 10px rgba(0, 255, 157, 0.5))" }} />
            <h1 className="text-2xl font-bold tracking-tight font-['Chivo']">
              Dinesh <span className="text-[#00FF9D]">Lead App</span>
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <StatusIndicator status={status.status} />
            <div className={`w-2 h-2 rounded-full ${isMonitoring ? 'bg-[#00FF9D] animate-ping' : 'bg-gray-600'}`} />
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Step-by-Step Control Panel */}
          <Card className="bg-black/40 backdrop-blur-xl border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-['Chivo'] flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#00FF9D]" />
                Control Panel
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              
              {/* Step 1: Login */}
              <div className="space-y-3 p-3 bg-white/5 rounded border border-white/10">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500/30">1</Badge>
                  Login to Generac Portal
                </div>
                <div className="space-y-2">
                  <Input
                    value={loginUrl}
                    onChange={(e) => setLoginUrl(e.target.value)}
                    className="bg-black/50 border-white/10 font-mono text-xs"
                    placeholder="Login URL"
                    disabled={!isIdle}
                  />
                  <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-black/50 border-white/10 font-mono text-xs"
                    placeholder="Email"
                    disabled={!isIdle}
                  />
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-black/50 border-white/10 font-mono text-xs pr-10"
                      placeholder="Password"
                      disabled={!isIdle}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <Button
                  onClick={doLogin}
                  disabled={isLoading || !isIdle}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  size="sm"
                >
                  {isLoading && isBusy ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
                  Login
                </Button>
              </div>

              {/* Step 2: Navigate */}
              <div className="space-y-3 p-3 bg-white/5 rounded border border-white/10">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/30">2</Badge>
                  Navigate to PowerPlay
                </div>
                <Button
                  onClick={doNavigate}
                  disabled={isLoading || !isLoggedIn}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                  size="sm"
                >
                  {isLoading && isBusy ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Navigation className="w-4 h-4 mr-2" />}
                  Go to PowerPlay
                </Button>
              </div>

              {/* Step 3: Monitor */}
              <div className="space-y-3 p-3 bg-white/5 rounded border border-white/10">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">3</Badge>
                  Start Monitoring
                </div>
                {!isMonitoring ? (
                  <Button
                    onClick={startMonitoring}
                    disabled={isLoading || !isLoggedIn}
                    className="w-full h-12 bg-[#00FF9D] hover:bg-[#00FF9D]/90 text-black font-bold uppercase tracking-wider shadow-[0_0_20px_rgba(0,255,157,0.4)]"
                  >
                    <Play className="w-5 h-5 mr-2" />
                    Start Monitoring
                  </Button>
                ) : (
                  <Button
                    onClick={stopMonitoring}
                    disabled={isLoading}
                    className="w-full h-12 bg-red-600/20 hover:bg-red-600/30 text-red-500 border border-red-500/30 font-bold uppercase"
                  >
                    <Square className="w-5 h-5 mr-2" />
                    Stop Monitoring
                  </Button>
                )}
              </div>

              {/* Stop All */}
              {!isIdle && (
                <Button
                  onClick={stopAll}
                  disabled={isLoading}
                  variant="ghost"
                  className="w-full text-gray-400 hover:text-red-400"
                  size="sm"
                >
                  End Session & Close Browser
                </Button>
              )}

              {/* Status */}
              <div className="text-xs font-mono text-gray-400 p-2 bg-black/30 rounded">
                {status.message}
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="space-y-4">
            <Card className="bg-black/40 backdrop-blur-xl border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">Uptime</p>
                    <p className="text-3xl font-mono font-bold text-[#00FF9D]">
                      {formatUptime(status.uptime_seconds)}
                    </p>
                  </div>
                  <Clock className="w-10 h-10 text-gray-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-black/40 backdrop-blur-xl border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">Leads Claimed</p>
                    <p className="text-3xl font-mono font-bold text-[#FF9F1C]">
                      {status.leads_claimed}
                    </p>
                  </div>
                  <Trophy className="w-10 h-10 text-gray-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-black/40 backdrop-blur-xl border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">Status</p>
                    <p className="text-lg font-mono font-semibold capitalize">
                      {status.status}
                    </p>
                  </div>
                  <Activity className={`w-10 h-10 ${isMonitoring ? 'text-[#00FF9D] animate-pulse' : 'text-gray-600'}`} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Logs */}
          <Card className="bg-black/40 backdrop-blur-xl border-white/10 lg:row-span-2">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-['Chivo'] flex items-center gap-2">
                <TerminalIcon className="w-5 h-5 text-[#00FF9D]" />
                Activity Log
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={clearLogs} className="text-gray-400 hover:text-white">
                <Trash2 className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[400px] bg-[#0D0D0D] border-t border-white/10">
                <div className="p-2">
                  {logs.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 font-mono text-sm">
                      No logs yet. Start the automation to see activity.
                    </div>
                  ) : (
                    logs.map((log) => <LogEntry key={log.id} log={log} />)
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Leads History */}
          <Card className="bg-black/40 backdrop-blur-xl border-white/10 lg:col-span-2">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-['Chivo'] flex items-center gap-2">
                <Trophy className="w-5 h-5 text-[#FF9F1C]" />
                Lead Claim History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[200px]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-xs uppercase text-gray-400">Timestamp</TableHead>
                      <TableHead className="text-xs uppercase text-gray-400">Lead ID</TableHead>
                      <TableHead className="text-xs uppercase text-gray-400">Status</TableHead>
                      <TableHead className="text-xs uppercase text-gray-400 text-right">Duration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-gray-500 font-mono text-sm">
                          No leads claimed yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      leads.map((lead) => (
                        <TableRow key={lead.id} className="border-white/5">
                          <TableCell className="font-mono text-xs text-gray-400">
                            {new Date(lead.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{lead.lead_id}</TableCell>
                          <TableCell>
                            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                              {lead.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs text-right text-gray-400">
                            {lead.duration_ms}ms
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        <footer className="mt-6 text-center text-xs text-gray-600 font-mono">
          Lead Commander v2.0 | Generac Dealer Portal Automation
        </footer>
      </div>
    </div>
  );
}

export default App;
